import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ExpensesData } from "@/types/fact-finder";

interface ExpensesSectionProps {
  data: ExpensesData | null;
  onSave: (data: ExpensesData) => Promise<void>;
  onNext: () => void;
}

const defaultData: ExpensesData = {
  groceries: 0,
  personal: 0,
  propertyOwnerOccupied: 0,
  propertyInvestment: 0,
  education: 0,
  childCare: 0,
  health: 0,
  transport: 0,
  insurance: 0,
  entertainment: 0,
  communication: 0,
  childSupport: 0,
  other: 0,
  otherDescription: "",
  totalMonthly: 0,
};

interface ExpenseCategory {
  key: keyof ExpensesData;
  label: string;
  description: string;
}

const expenseCategories: ExpenseCategory[] = [
  {
    key: "groceries",
    label: "Groceries & Household",
    description: "Food, cleaning supplies, toiletries",
  },
  {
    key: "personal",
    label: "Personal & Clothing",
    description: "Clothing, haircuts, personal care",
  },
  {
    key: "propertyOwnerOccupied",
    label: "Property - Owner Occupied",
    description: "Rates, body corp, maintenance (your home)",
  },
  {
    key: "propertyInvestment",
    label: "Property - Investment",
    description: "Rates, management, maintenance (rental properties)",
  },
  {
    key: "education",
    label: "Education",
    description: "School fees, books, tutoring",
  },
  {
    key: "childCare",
    label: "Child Care",
    description: "Daycare, before/after school care",
  },
  {
    key: "health",
    label: "Health & Medical",
    description: "Doctor visits, prescriptions, health insurance",
  },
  {
    key: "transport",
    label: "Transport",
    description: "Fuel, public transport, parking, registration",
  },
  {
    key: "insurance",
    label: "Insurance",
    description: "Car, home, life, income protection",
  },
  {
    key: "entertainment",
    label: "Entertainment & Recreation",
    description: "Dining out, streaming, hobbies, holidays",
  },
  {
    key: "communication",
    label: "Communication",
    description: "Phone, internet, streaming subscriptions",
  },
  {
    key: "childSupport",
    label: "Child Support / Alimony",
    description: "Court-ordered payments",
  },
];

export function ExpensesSection({ data, onSave, onNext }: ExpensesSectionProps) {
  const [formData, setFormData] = useState<ExpensesData>(data || defaultData);
  const [isSaving, setIsSaving] = useState(false);

  const formatCurrency = (value: number | undefined): string => {
    if (!value) return "";
    return value.toLocaleString();
  };

  const parseCurrency = (value: string): number => {
    return parseInt(value.replace(/,/g, ""), 10) || 0;
  };

  const updateField = (field: keyof ExpensesData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Calculate total whenever expenses change
  useEffect(() => {
    const total =
      formData.groceries +
      formData.personal +
      formData.propertyOwnerOccupied +
      formData.propertyInvestment +
      formData.education +
      formData.childCare +
      formData.health +
      formData.transport +
      formData.insurance +
      formData.entertainment +
      formData.communication +
      formData.childSupport +
      formData.other;

    setFormData((prev) => ({ ...prev, totalMonthly: total }));
  }, [
    formData.groceries,
    formData.personal,
    formData.propertyOwnerOccupied,
    formData.propertyInvestment,
    formData.education,
    formData.childCare,
    formData.health,
    formData.transport,
    formData.insurance,
    formData.entertainment,
    formData.communication,
    formData.childSupport,
    formData.other,
  ]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Monthly Expenses</h2>
        <p className="text-muted-foreground mt-1">
          Provide your average monthly living expenses. These should be for your household (both applicants combined).
        </p>
      </div>

      {/* HEM Note */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Lenders use the Household Expenditure Measure (HEM) as a minimum benchmark.
          If your declared expenses are lower than HEM, lenders will use HEM instead.
          Please provide accurate figures to avoid issues during assessment.
        </p>
      </div>

      {/* Expense Categories */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Living Expenses</h3>
        <p className="text-sm text-muted-foreground">
          All amounts should be monthly. Do not include loan repayments (these were captured in liabilities).
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {expenseCategories.map((category) => (
            <div key={category.key} className="p-4 bg-muted/30 rounded-lg space-y-2">
              <label className="text-sm font-medium">{category.label}</label>
              <Input
                value={formatCurrency(formData[category.key] as number)}
                onChange={(e) => updateField(category.key, parseCurrency(e.target.value))}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">{category.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Other Expenses */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Other Expenses</h3>

        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Other Monthly Expenses ($)</label>
              <Input
                value={formatCurrency(formData.other)}
                onChange={(e) => updateField("other", parseCurrency(e.target.value))}
                placeholder="0"
              />
            </div>

            {formData.other > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Please describe</label>
                <Input
                  value={formData.otherDescription || ""}
                  onChange={(e) => updateField("otherDescription", e.target.value)}
                  placeholder="e.g. Pet expenses, gym membership"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expense Summary */}
      <div className="p-6 bg-primary/5 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Total Monthly Expenses</h3>
            <p className="text-sm text-muted-foreground">Combined living expenses (excluding loan repayments)</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold">${formatCurrency(formData.totalMonthly)}</p>
            <p className="text-sm text-muted-foreground">
              ~${formatCurrency(formData.totalMonthly * 12)}/year
            </p>
          </div>
        </div>
      </div>

      {/* Expense Breakdown */}
      {formData.totalMonthly > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium">Expense Breakdown</h4>
          <div className="space-y-2">
            {expenseCategories
              .filter((c) => (formData[c.key] as number) > 0)
              .sort((a, b) => (formData[b.key] as number) - (formData[a.key] as number))
              .map((category) => {
                const value = formData[category.key] as number;
                const percentage = Math.round((value / formData.totalMonthly) * 100);
                return (
                  <div key={category.key} className="flex items-center gap-4">
                    <span className="text-sm w-32 truncate">{category.label}</span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-20 text-right">
                      ${formatCurrency(value)}
                    </span>
                    <span className="text-sm text-muted-foreground w-12 text-right">
                      {percentage}%
                    </span>
                  </div>
                );
              })}
            {formData.other > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-sm w-32 truncate">Other</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.round((formData.other / formData.totalMonthly) * 100)}%` }}
                  />
                </div>
                <span className="text-sm font-medium w-20 text-right">
                  ${formatCurrency(formData.other)}
                </span>
                <span className="text-sm text-muted-foreground w-12 text-right">
                  {Math.round((formData.other / formData.totalMonthly) * 100)}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}
